import React, { Component } from 'react';

import EventStack from 'active-event-stack';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import centerComponent from 'react-center-component';
import classNames from 'classnames';
import dynamics from 'dynamics.js';
import { inject } from 'narcissus';
import keycode from 'keycode';

const styles = {
  dialog: {
    boxSizing: 'border-box',
    position: 'relative',
    background: 'white',
    padding: 0,
    color: '#333',
    boxShadow: `0 0 2px 0 rgba(0,0,0,.1)`,
    borderRadius: 3,
  },
  closeButton: {
    position: 'absolute',
    top: 0,
    left: -500,
    display: 'block',
    width: 500,
    minHeight: 500,
    transition: 'transform 0.1s',
    '&&:hover': { transform: 'scale(1.1)' },
  },
};

// This decorator centers the dialog
class ModalDialog extends Component {
  constructor(props) {
    super(props);

    this.state = { didAnimateInAlready: false };

    this.shouldClickDismiss = this.shouldClickDismiss.bind(this);
    this.handleGlobalClick = this.handleGlobalClick.bind(this);
    this.handleGlobalKeydown = this.handleGlobalKeydown.bind(this);
    this.animateIn = this.animateIn.bind(this);
  }

  componentWillMount() {
    /**
     * This is done in the componentWillMount instead of the componentDidMount
     * because this way, a modal that is a child of another will have register
     * for events after its parent
     */
    this.eventToken = EventStack.addListenable([
      ['click', this.handleGlobalClick],
      ['keydown', this.handleGlobalKeydown],
    ]);
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.topOffset !== null && this.props.topOffset === null) {
      // This means we are getting top information for the first time
      if (!this.state.didAnimateInAlready) {
        // Double check we have not animated in yet
        this.animateIn();
      }
    }

    if (nextProps.componentIsLeaving && !this.props.componentIsLeaving) {
      const node = ReactDOM.findDOMNode(this);
      dynamics.animate(
        node,
        { scale: 1.2, opacity: 0 },
        {
          duration: 300,
          type: dynamics.easeIn,
        },
      );
    }
  }

  componentWillUnmount() {
    EventStack.removeListenable(this.eventToken);
  }

  shouldClickDismiss(event) {
    const { target } = event;
    // This piece of code isolates targets which are fake clicked by things
    // like file-drop handlers
    if (target.tagName === 'INPUT' && target.type === 'file') {
      return false;
    }

    if (target === this.refs.self || this.refs.self.contains(target)) {
      return false;
    }
    return true;
  }

  handleGlobalClick(event) {
    if (this.shouldClickDismiss(event)) {
      if (typeof this.props.onClose === 'function') {
        this.props.onClose();
      }
    }
  }

  handleGlobalKeydown(event) {
    if (keycode(event) === 'esc') {
      if (typeof this.props.onClose === 'function') {
        this.props.onClose();
      }
    }
  }

  animateIn() {
    this.setState({ didAnimateInAlready: true }, () => {
      // Animate this node once it is mounted
      const node = ReactDOM.findDOMNode(this);

      // This sets the scale...
      if (document.body.style.transform === null) {
        node.style.WebkitTransform = 'scale(0.5)';
      } else {
        node.style.transform = 'scale(0.5)';
      }

      dynamics.animate(
        node,
        { scale: 1 },
        {
          type: dynamics.spring,
          duration: 500,
          friction: 400,
        },
      );
    });
  }

  render() {
    const {
      children,
      className,
      componentIsLeaving,
      left,
      leftOffset,
      margin,
      recenter,
      style,
      top,
      topOffset,
      width,
      ...rest
    } = this.props;

    const dialogStyle = {
      position: 'absolute',
      marginBottom: margin,
      width: width,
      top: Math.max(topOffset, margin),
      left: leftOffset,
      ...style,
    };

    const divClassName = classNames(inject(styles.dialog), className);
    delete rest.onClose;

    return (
      <div {...rest} ref="self" className={divClassName} style={dialogStyle}>
        {children}
      </div>
    );
  }
}

ModalDialog.defaultProps = { width: 'auto', margin: 20 };

ModalDialog.propTypes = {
  onClose: PropTypes.func,
  // required for the close button
  className: PropTypes.string,
  // css class in addition to .ReactModalDialog
  width: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  // width
  topOffset: PropTypes.number,
  // injected by @centerComponent
  leftOffset: PropTypes.number,
  // injected by @centerComponent
  margin: PropTypes.number.isRequired,
  // the margin around the dialog
  children: PropTypes.node,
  componentIsLeaving: PropTypes.bool,
  style: PropTypes.object,
  left: PropTypes.number,
  recenter: PropTypes.func.isRequired,
  top: PropTypes.number,
};

export default centerComponent(ModalDialog);



// WEBPACK FOOTER //
// ./src/components/modalDialog.js